import { useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { FEATURED_FILMS } from "@/data/content";
import { Play, Maximize } from "lucide-react";

const FilmCard = ({ item, registerRef, onPlay }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  const setRef = (el) => {
    ref.current = el;
    registerRef(item.id, el, setPlaying);
  };

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      onPlay(item.id);
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const fullscreen = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <Reveal className="group">
      <div
        data-testid={`featured-film-${item.id}`}
        className="relative overflow-hidden bg-secondary cursor-pointer film-grain"
        onClick={toggle}
      >
        <div className="aspect-[9/16] w-full overflow-hidden">
          <video
            ref={setRef}
            playsInline
            preload="metadata"
            poster={item.poster}
            onEnded={() => setPlaying(false)}
            className="w-full h-full object-cover"
          >
            <source src={item.video} type="video/mp4" />
          </video>
        </div>

        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent transition-opacity duration-500 ${
            playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
          }`}
        />

        <div
          className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 ${
            playing ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="w-16 h-16 rounded-full border border-[#F7F5F0]/60 flex items-center justify-center text-[#F7F5F0] backdrop-blur-sm">
            <Play strokeWidth={1} className="ml-0.5" />
          </div>
        </div>

        <div
          className={`absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between z-[2] transition-opacity duration-500 ${
            playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
          }`}
        >
          <div>
            <h3 className="font-display font-light text-2xl sm:text-3xl text-[#F7F5F0] leading-none">
              {item.title}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={`featured-fullscreen-${item.id}`}
              onClick={fullscreen}
              aria-label="Fullscreen"
              className="w-10 h-10 rounded-full border border-[#F7F5F0]/50 flex items-center justify-center text-[#F7F5F0] backdrop-blur-sm hover:bg-[#F7F5F0]/15 transition-colors"
            >
              <Maximize strokeWidth={1.25} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export const FeaturedFilms = () => {
  const refs = useRef({});

  const registerRef = (id, el, setPlaying) => {
    refs.current[id] = { el, setPlaying };
  };

  const onPlay = (id) => {
    Object.entries(refs.current).forEach(([key, { el, setPlaying }]) => {
      if (key !== id && el && !el.paused) {
        el.pause();
        setPlaying(false);
      }
    });
  };

  return (
    <div
      id="reel"
      data-testid="featured-films"
      className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-7 mb-16 sm:mb-24 scroll-mt-24"
    >
      {FEATURED_FILMS.map((item) => (
        <FilmCard
          key={item.id}
          item={item}
          registerRef={registerRef}
          onPlay={onPlay}
        />
      ))}
    </div>
  );
};

export default FeaturedFilms;
